import { supabase } from '../supabaseClient';

/**
 * Word Service with In-Memory Caching
 * Bridge between the Supabase 'words' table and the application.
 * Designed to provide a compatible interface for the MASTER_WORDS migration
 * while ensuring zero lag during gameplay via intelligent caching.
 */

// In-memory cache to prevent redundant fetches and lag during gameplay
let cachedWords = null;
let isFetching = false;
let fetchPromise = null;

let cachedRelationships = null;
let isFetchingRelationships = false;
let relationshipFetchPromise = null;

let cachedAllRelationships = null;
let isFetchingAllRelationships = false;
let allRelationshipsFetchPromise = null;

/**
 * Helper to normalize database rows to the format expected by the frontend logic.
 * Maps 'group_id' to 'group' for compatibility with existing MASTER_WORDS structure.
 */
const normalizeWord = (w) => ({
  ...w,
  group: w.group_id // Ensure compatibility with existing MASTER_WORDS structure
});

/**
 * Fetches all active words from the database, utilizing the in-memory cache.
 */
export const fetchAllWords = async () => {
  // 1. Return cached words if available
  if (cachedWords) {
    return cachedWords;
  }

  // 2. Prevent multiple simultaneous fetches if called concurrently during boot
  if (isFetching && fetchPromise) {
    return fetchPromise;
  }

  isFetching = true;
  
  // 3. Perform the actual fetch
  fetchPromise = (async () => {
    try {
      const { data, error } = await supabase
        .from('words')
        .select('*')
        .eq('is_active', true)
        .order('word', { ascending: true });

      if (error) {
        console.error('[wordService] Error fetching all words:', error.message);
        isFetching = false;
        fetchPromise = null;
        return [];
      }

      // Normalize and cache the results
      cachedWords = (data || []).map(normalizeWord);
      isFetching = false;
      return cachedWords;
      
    } catch (err) {
      console.error('[wordService] Unexpected error in fetchAllWords:', err);
      isFetching = false;
      fetchPromise = null;
      return [];
    }
  })();

  return fetchPromise;
};

/**
 * Fetches all synonym relationships from the database and resolves them into full word objects.
 */
export const fetchSynonymRelationships = async () => {
  if (cachedRelationships) {
    return cachedRelationships;
  }

  if (isFetchingRelationships && relationshipFetchPromise) {
    return relationshipFetchPromise;
  }

  isFetchingRelationships = true;

  relationshipFetchPromise = (async () => {
    try {
      // 1. Fetch the raw relationships
      const { data: rels, error } = await supabase
        .from('word_relationships')
        .select('*')
        .eq('relationship_type', 'synonym');

      if (error) {
        console.error('[wordService] Error fetching synonym relationships:', error.message);
        isFetchingRelationships = false;
        relationshipFetchPromise = null;
        return [];
      }


      // 2. Resolve relationships against the master word list
      const allWords = await fetchAllWords();
      const wordMap = new Map(allWords.map(w => [w.id, w]));

      // 3. Construct resolved relationship objects with canonical deduplication
      const seenPairKeys = new Set();
      let afterResolutionCount = 0;
      const resolved = (rels || [])
        .map(r => {
          const w1 = wordMap.get(r.word_id);
          const w2 = wordMap.get(r.related_word_id);
          
          if (!w1 || !w2) return null;

          // Deduplicate reversed pairs (e.g., A↔B and B↔A are the same relationship)
          const pairKey = [r.word_id, r.related_word_id].sort().join(':');
          if (seenPairKeys.has(pairKey)) return null;
          seenPairKeys.add(pairKey);

          afterResolutionCount++;
          
          // Ensure at least one word in the pair is intended for Match the Twins
          if (!w1.used_in_match_the_twins && !w2.used_in_match_the_twins) return null;

          return {
            ...r,
            word: w1,
            related_word: w2
          };
        })
        .filter(Boolean);


      cachedRelationships = resolved;
      isFetchingRelationships = false;
      return cachedRelationships;
      
    } catch (err) {
      console.error('[wordService] Unexpected error in fetchSynonymRelationships:', err);
      isFetchingRelationships = false;
      relationshipFetchPromise = null;
      return [];
    }
  })();

  return relationshipFetchPromise;
};

/**
 * Fetches all word relationships (synonym, antonym, connotation_clash)
 * and resolves them against the master word list.
 */
export const fetchAllRelationships = async () => {
  if (cachedAllRelationships) {
    return cachedAllRelationships;
  }

  if (isFetchingAllRelationships && allRelationshipsFetchPromise) {
    return allRelationshipsFetchPromise;
  }

  isFetchingAllRelationships = true;

  allRelationshipsFetchPromise = (async () => {
    try {
      const { data: rels, error } = await supabase
        .from('word_relationships')
        .select('*');

      if (error) {
        console.error('[wordService] Error fetching all relationships:', error.message);
        isFetchingAllRelationships = false;
        allRelationshipsFetchPromise = null;
        return [];
      }

      const allWords = await fetchAllWords();
      const wordMap = new Map(allWords.map(w => [w.id, w]));

      const resolved = (rels || [])
        .map(r => {
          const w1 = wordMap.get(r.word_id);
          const w2 = wordMap.get(r.related_word_id);
          if (!w1 || !w2) return null;
          return {
            ...r,
            word: w1,
            related_word: w2
          };
        })
        .filter(Boolean);

      cachedAllRelationships = resolved;
      isFetchingAllRelationships = false;
      return cachedAllRelationships;
      
    } catch (err) {
      console.error('[wordService] Unexpected error in fetchAllRelationships:', err);
      isFetchingAllRelationships = false;
      allRelationshipsFetchPromise = null;
      return [];
    }
  })();

  return allRelationshipsFetchPromise;
};

/**
 * Fetches the new simplified Tone Clash relationships.
 * These map a source word ID to a simple antonym string.
 */
export const fetchToneClashRelationships = async () => {
  try {
    const { data, error } = await supabase
      .from('tone_clash_relationships')
      .select('*');

    if (error) {
      console.error('[wordService] Error fetching tone clash relationships:', error.message);
      return [];
    }
    return data || [];
  } catch (err) {
    console.error('[wordService] Unexpected error in fetchToneClashRelationships:', err);
    return [];
  }
};

/**
 * Fetches only words marked for Visualization mode.
 * Uses the cached master list to avoid redundant database calls.
 */
export const fetchVisualizationWords = async () => {
  try {
    const allWords = await fetchAllWords();
    return allWords.filter(w => w.used_in_visualization === true);
  } catch (err) {
    console.error('[wordService] Unexpected error in fetchVisualizationWords:', err);
    return [];
  }
};

/**
 * Fetches only words marked for Connotations mode.
 * Uses the cached master list to avoid redundant database calls.
 */
export const fetchConnotationWords = async () => {
  try {
    const allWords = await fetchAllWords();
    return allWords.filter(w => w.used_in_connotations === true);
  } catch (err) {
    console.error('[wordService] Unexpected error in fetchConnotationWords:', err);
    return [];
  }
};

/**
 * Fetches a specific group of words.
 * Uses the cached master list to avoid redundant database calls.
 */
export const fetchWordsByGroup = async (groupId) => {
  try {
    const allWords = await fetchAllWords();
    return allWords.filter(w => w.group === groupId);
  } catch (err) {
    console.error(`[wordService] Unexpected error in fetchWordsByGroup for group ${groupId}:`, err);
    return [];
  }
};

/**
 * Utility to manually clear the cache if a hard refresh is needed.
 */
export const clearWordCache = () => {
  cachedWords = null;
  isFetching = false;
  fetchPromise = null;
  cachedRelationships = null;
  isFetchingRelationships = false;
  relationshipFetchPromise = null;
  cachedAllRelationships = null;
  isFetchingAllRelationships = false;
  allRelationshipsFetchPromise = null;
};
